import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function SettingsPanel({ open, onClose }: SettingsPanelProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 998,
            background: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999,
          width: 320, background: "var(--dyna-bg-elevated)",
          borderLeft: "1px solid var(--dyna-border)",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 280ms cubic-bezier(0.16, 1, 0.3, 1)",
          overflowY: "auto", display: "flex", flexDirection: "column",
          boxShadow: "-24px 0 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--dyna-border)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{
            fontFamily: "Coolvetica, system-ui, sans-serif",
            fontSize: 18, fontWeight: 700, letterSpacing: "0.04em",
            textTransform: "uppercase", color: "var(--dyna-fg)",
          }}>Settings</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: "50%",
              background: "var(--ctrl-bg)", border: "1px solid var(--ctrl-border)",
              color: "var(--ctrl-fg)", cursor: "pointer",
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 32 }}>

          {/* 1. ACCOUNT */}
          <div>
            <p style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              letterSpacing: "0.2em", textTransform: "uppercase",
              color: "var(--lavender-500)", marginBottom: 12, margin: "0 0 12px",
            }}>Account</p>
            <button
              className="dyna-btn dyna-btn--secondary"
              style={{ width: "100%", justifyContent: "center", fontSize: 13 }}
              onClick={() => { onClose(); navigate("/login"); }}
            >
              Log In
            </button>
          </div>

          {/* 2. LANGUAGE */}
          <div>
            <p style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              letterSpacing: "0.2em", textTransform: "uppercase",
              color: "var(--lavender-500)", margin: "0 0 12px",
            }}>Language</p>
            <div style={{ display: "flex", gap: 8 }}>
              {["en", "he"].map(lang => (
                <button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  style={{
                    flex: 1, padding: "8px", borderRadius: 999, fontSize: 13, fontWeight: 500,
                    background: i18n.language === lang ? "var(--dyna-brand)" : "var(--ctrl-bg)",
                    color: i18n.language === lang ? "var(--dyna-fg)" : "var(--ctrl-fg)",
                    border: "1px solid",
                    borderColor: i18n.language === lang ? "var(--dyna-brand)" : "var(--ctrl-border)",
                    cursor: "pointer", transition: "all 200ms",
                  }}
                >
                  {lang === "en" ? "English" : "עברית"}
                </button>
              ))}
            </div>
          </div>

          {/* 5. NOTIFICATIONS */}
          <div>
            <p style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              letterSpacing: "0.2em", textTransform: "uppercase",
              color: "var(--lavender-500)", margin: "0 0 12px",
            }}>Notifications</p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, color: "var(--dyna-fg-muted)" }}>Push notifications</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
                  color: "var(--lavender-500)", background: "rgba(135,115,180,0.12)",
                  border: "1px solid rgba(135,115,180,0.2)", padding: "2px 8px", borderRadius: 999,
                }}>Soon</span>
                <div style={{
                  width: 36, height: 20, borderRadius: 999,
                  background: "var(--ctrl-bg)", border: "1px solid var(--ctrl-border)",
                  opacity: 0.4, cursor: "not-allowed",
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
